import React, {useEffect, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import {estateFlowchartApiClient, ResponseStatus} from "src/EstateFlowchart/client/EstateFlowchartApiClient";
import {ModalSource, RevocableTrustModalInputs} from "src/EstateFlowchart/model/EstateFlowchartTypes";
import ModalWrapper from "../../../components/Modal/ModalWrapper/ModalWrapper";
import {RouteWithId} from "../../../routes/types";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useAnalyticsTrackers} from "../../../utils/useAnalyticsTrackers";
import {
    familyMembersSelector,
    getEstateFlowchartsSelector,
    selectedFlowchartIdSelector,
    setEstateFlowchartState,
    setGenericErrorModal,
    setSelectedFlowchartId
} from "../../slices/EstateFlowChartSlice";
import {isFlowchartNameDuplicate, isValid} from "../../validation/ValidationUtils";
import './_modal-flowchart.scss';
import FlowchartModalContent from "./FlowchartModalContent";

type AddFlowchartModalProps = {
    isOpen: boolean,
    handleCancel: () => void;
    modalSource: ModalSource,
    flowchartIdToDuplicate?: string
}

const AddFlowchartModal: React.FC<AddFlowchartModalProps> = ({
                                                                 isOpen,
                                                                 handleCancel,
                                                                 modalSource,
                                                                 flowchartIdToDuplicate,
                                                                 ...restOfProps
                                                             }) => {
    const history = useHistory();
    const {id: profileId} = useParams<RouteWithId>();
    const familyMembers = useAppSelector(familyMembersSelector);
    const flowchartId = useAppSelector(selectedFlowchartIdSelector);
    const {primaryMember} = familyMembers;
    const {page} = useAnalyticsTrackers();
    const dispatch = useAppDispatch();
    const selector = useAppSelector;
    const flowcharts = selector(getEstateFlowchartsSelector);

    // Memoize initial data to avoid unnecessary re-creation
    const getInitialFlowchartData = React.useCallback((): RevocableTrustModalInputs => ({
        flowchartName: "",
        estateItem: undefined,
        revTrustType: [],
        assumedFirstDeath: "",
        distributionStructure: "",
    }), [primaryMember?.id, primaryMember?.firstName, primaryMember?.lastName]);

    const [flowchartData, setFlowchartData] = useState(getInitialFlowchartData());
    const [saveHasBeenClicked, setSaveHasBeenClicked] = useState(false);
    const estateFlowcharts = useAppSelector(getEstateFlowchartsSelector);

    useEffect(() => {
        if (isOpen) {
            setFlowchartData(getInitialFlowchartData());
        }
    }, [getInitialFlowchartData, isOpen]);

    const isFlowchartDataValid = (data: RevocableTrustModalInputs) => {
        if (modalSource === ModalSource.AddEstateItem) {
            return data?.estateItem !== undefined;
        }
        if (modalSource === ModalSource.DuplicateFlowchart) {
            return data?.flowchartName !== "" && isValid(data.flowchartName) && !isFlowchartNameDuplicate(estateFlowcharts, data?.flowchartName ?? "");
        }
        return data?.flowchartName !== "" && isValid(data.flowchartName) && data?.estateItem !== undefined && !isFlowchartNameDuplicate(estateFlowcharts, data?.flowchartName ?? "");
    };

    const handleCancelClick = () => {
        setSaveHasBeenClicked(false);
        setFlowchartData(getInitialFlowchartData());
        handleCancel();
    };

    /**
     * Handles the Next/Duplicate button click for the modal.
     * - Duplicates flowchart if source is DuplicateFlowchart
     * - Navigates to next step for AddFlowchart/AddEstateItem
     * @returns {boolean} True if action was performed, false otherwise
     */
    const onClickNext = (): boolean => {
        setSaveHasBeenClicked(true);

        if (modalSource === ModalSource.DuplicateFlowchart) {
            if (!isFlowchartDataValid(flowchartData) || !flowchartIdToDuplicate) return false;
            handleDuplicateFlowchart();
            return true;
        }

        page('DEF_Click_Add_Flowchart');
        if (!isFlowchartDataValid(flowchartData)) return false;

        const estateItemType = flowchartData.estateItem?.valueOf().replace(/ /g, "");
        const existingFlowchartIdOrNew = modalSource === ModalSource.AddEstateItem ? flowchartId : "new";
        history.push(
            `/Profile/${profileId}/ClientProfile/EstateFlowchart/${existingFlowchartIdOrNew}/${estateItemType}/new/BasicInformation`,
            flowchartData
        );
        return true;
    };

    /**
     * Duplicates the flowchart and updates state accordingly.
     */
    const handleDuplicateFlowchart = async (): Promise<void> => {
        if (!flowchartIdToDuplicate) return;
        try {
            const response: ResponseStatus = await estateFlowchartApiClient.duplicateFlowchart(
                flowchartData.flowchartName!,
                profileId,
                flowchartIdToDuplicate
            );
            handleCancelClick();
            if (response.isSuccessStatusCode) {
                dispatch(setEstateFlowchartState([...flowcharts, response.data]));
                dispatch(setSelectedFlowchartId(response.data.flowchartId ?? ""));
            } else {
                dispatch(setGenericErrorModal({
                    isOpen: true,
                    operationId: response.traceId
                }));
            }
        } catch (error) {
            handleCancelClick();
            dispatch(setGenericErrorModal({
                isOpen: true,
                operationId: "unknown-error"
            }));
        }
    };

    //Determine Add vs Add Estate Item
    const isFlowchartOrEstateHeader = () => {
        return modalSource === ModalSource.AddFlowchart ? "Add Flowchart" : "Add Estate Item";
    }

    const isFlowchartOrEstateSubHeader = () => {
        return modalSource === ModalSource.AddEstateItem ? "This will add the selected item to the current flowchart. You can modify or remove it at any time." : undefined;
    }

    const isFlowchartOrEstateClass = () => {
        return modalSource === ModalSource.AddFlowchart ? 'add-flowchart' : 'add-estate-item'
    }

    const headerText = modalSource === ModalSource.DuplicateFlowchart ? "Duplicate Flowchart" : isFlowchartOrEstateHeader();
    const subHeaderText = modalSource === ModalSource.DuplicateFlowchart ? "You are duplicating an existing flowchart. Please enter a new Flowchart Name below. Flowchart Names cannot be the same."
        : isFlowchartOrEstateSubHeader();
    const modalClass = modalSource === ModalSource.DuplicateFlowchart ? "duplicate-flowchart" : isFlowchartOrEstateClass();

    return (
        <ModalWrapper
            id={`flowchart-stepper-form-${modalClass}`}
            headerText={headerText}
            subHeaderText={subHeaderText}
            dataTestId={"flowchart-stepper-form_testId"}
            isOpen={isOpen}
            showCloseButton={true}
            onRequestClose={handleCancelClick}
            buttons={
                [
                    {
                        text: "CANCEL",
                        onClick: handleCancelClick,
                        className: "flowchart-modal-footer-button"
                    },
                    {
                        text: modalSource === ModalSource.DuplicateFlowchart ? "DUPLICATE" : "NEXT",
                        onClick: onClickNext,
                        primary: true
                    }
                ]
            }
        >
            <FlowchartModalContent modalSource={modalSource}
                                   modalInputs={flowchartData}
                                   setFlowchartData={setFlowchartData}
                                   saveHasBeenClicked={saveHasBeenClicked}
                                   {...restOfProps}/>
        </ModalWrapper>
    );
};

export default AddFlowchartModal;